/**
 * SectionRegistry — the single source of truth for section components, supported
 * layouts, default layouts, and content capabilities (adjustment C). The Builder Engine,
 * validation, the AI contract, and the Manual Editor all read layout rules from here;
 * none hardcode layout lists. See CONTRACTS_FROZEN.md §6.
 */
import type { SectionKind, LayoutId } from '../schema'
import type {
  ContentCapabilities,
  RegistryEntry,
  SectionRegistry,
  SiteComponent,
} from './types'

/** Create an empty registry. Builtins are registered by `registerBuiltins`. */
export function createSectionRegistry(): SectionRegistry {
  const entries = new Map<SectionKind, RegistryEntry>()

  return {
    register<K extends SectionKind>(kind: K, entry: RegistryEntry<K>): void {
      // Widen to the erased RegistryEntry for storage; the public generic signature keeps
      // (kind, entry) type-checked at every call site.
      entries.set(kind, entry as unknown as RegistryEntry)
    },

    entry(kind: SectionKind): RegistryEntry | undefined {
      return entries.get(kind)
    },

    resolve(kind: SectionKind): SiteComponent | undefined {
      return entries.get(kind)?.component
    },

    isValidLayout(kind: SectionKind, layout: LayoutId): boolean {
      const supported = entries.get(kind)?.supportedLayouts as readonly LayoutId[] | undefined
      return supported?.includes(layout) ?? false
    },

    defaultLayout(kind: SectionKind): LayoutId | undefined {
      return entries.get(kind)?.defaultLayout as LayoutId | undefined
    },

    capabilities(kind: SectionKind): ContentCapabilities | undefined {
      return entries.get(kind)?.contentCapabilities
    },
  }
}
